import { all, call, delay, fork, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actionTypes from 'appRedux/actions/chargeSchedule/ChargeInhouseActionType';
import * as actionCreators from 'appRedux/actions/chargeSchedule/ChargeInhouse';
import { userSignOut } from 'appRedux/actions/Auth';
import { API_URL_V1, API_PMS_HEADERS } from 'constants/ApiSetting';
import { APIGetRequest, APIPostRequest } from 'util/connection';
import { getItems } from 'util/localStorage';
import { ApiResponse } from 'constants/ActionTypes';
import {
  chargeScheduleType,
  RateBreakdownGroup,
  RateScheduleInhouseRequest,
} from 'appRedux/reducers/chargeSchedule/chargeType';
import { buildRateUpdatePayloads } from './rateUpdatePayload';
import { executeRateUpdateRequests } from './rateUpdateRequest';

const getAuthorizationHeaders = function* () {
  const { access_token } = yield call(getItems, ['access_token']);
  API_PMS_HEADERS.headers.Authorization = `Bearer ${access_token}`;
  return API_PMS_HEADERS;
};

const normalizeRequest = (payload: any): RateScheduleInhouseRequest =>
  typeof payload === 'object' && payload !== null
    ? payload
    : { registerID: Number(payload) };

const getRpcError = (response: ApiResponse, fallback: string) => {
  const data = response?.data || {};
  return {
    message: data.result_msg || data.message || fallback,
    result_code: data.result_code || 'ERROR',
    msg_code: data.msg_code,
  };
};

function* chargeInhouseRequestProcess({ payload }: actionTypes.ChargeInhouseRequest) {
  const request = normalizeRequest(payload);

  try {
    const headers = yield* getAuthorizationHeaders();
    const response: ApiResponse = yield APIPostRequest(
      `${API_URL_V1}pms/rpc/registration/inhouse/rate_schedule/get`,
      { register_id: Number(request.registerID) },
      headers
    );
    const { result_code, result_data } = response.data || {};

    if (response.status === 200 && result_code === '0') {
      const result: chargeScheduleType[] = Array.isArray(result_data) ? result_data : [];
      yield put(actionCreators.chargeInhouseDataSourceSuccess(result));
    } else {
      const error = getRpcError(response, 'Unable to load rate schedule');
      yield put(actionCreators.chargeInhouseDataSourceSuccess([]));
      yield put(
        actionCreators.chargeInhouseProcessSuccess({
          status: false,
          action: 'rateSchedule',
          ...error,
        })
      );
      if (response.status === 403 && response.data?.data) yield put(userSignOut());
    }
  } catch (error) {
    yield put(actionCreators.chargeInhouseDataSourceSuccess([]));
    yield put(
      actionCreators.chargeInhouseProcessSuccess({
        status: false,
        action: 'rateSchedule',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeInhouseBreakdownProcess({ payload }: actionTypes.ChargeInhouseBreakdownRequest) {
  const request = normalizeRequest(payload);

  try {
    const headers = yield* getAuthorizationHeaders();
    const response: ApiResponse = yield APIPostRequest(
      `${API_URL_V1}pms/rpc/registration/inhouse/rate_breakdown/get`,
      { register_id: Number(request.registerID) },
      headers
    );
    const { result_code, result_data } = response.data || {};

    if (response.status === 200 && result_code === '0') {
      const result: RateBreakdownGroup[] = Array.isArray(result_data) ? result_data : [];
      yield put(actionCreators.chargeInhouseBreakdownSuccess(result));
    } else {
      const error = getRpcError(response, 'Unable to load rate breakdown');
      yield put(actionCreators.chargeInhouseBreakdownSuccess([]));
      yield put(
        actionCreators.chargeInhouseProcessSuccess({
          status: false,
          action: 'rateBreakdown',
          ...error,
        })
      );
      if (response.status === 403 && response.data?.data) yield put(userSignOut());
    }
  } catch (error) {
    yield put(actionCreators.chargeInhouseBreakdownSuccess([]));
    yield put(
      actionCreators.chargeInhouseProcessSuccess({
        status: false,
        action: 'rateBreakdown',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeInhouseUpdateProcess({ payload }: actionTypes.ChargeInhouseUpdate) {
  const { registerID, inlineUpdates = [], trans = [], valueUpdate = {} } = payload || {};
  const updateRequests = buildRateUpdatePayloads({
    context: { register_id: Number(registerID) },
    inlineUpdates,
    transactions: trans,
    valueUpdate,
  });

  try {
    const headers = yield* getAuthorizationHeaders();
    const { success, result } = yield* executeRateUpdateRequests(
      `${API_URL_V1}pms/rpc/registration/inhouse/rate_schedule/update`,
      updateRequests,
      headers
    );

    yield put(
      actionCreators.chargeInhouseProcessSuccess({
        status: success,
        action: 'updateRateSchedule',
        message:
          result.result_msg || (success ? 'Save success' : 'Unable to save rate schedule'),
        result_code: result.result_code || 'ERROR',
        msg_code: result.msg_code,
      })
    );
  } catch (error) {
    yield put(
      actionCreators.chargeInhouseProcessSuccess({
        status: false,
        action: 'updateRateSchedule',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeInhouseDetailProcess({ payload }: actionTypes.ChargeInhouseDetailRequest) {
  const headers = yield* getAuthorizationHeaders();
  const url = `${API_URL_V1}pms/registration/guestInHouse/${payload}`;

  try {
    const response: ApiResponse = yield APIGetRequest(url, headers);
    if (response.status === 200) {
      const { guests, status, extra_adult, extra_child, extra_infant } = response.data;
      yield put(
        actionCreators.chargeInhouseDetailDataSourceSuccess({
          full_name: guests?.[0]?.full_name || null,
          status,
          extra_adult,
          extra_child,
          extra_infant,
        })
      );
    } else if (response.status === 403 && response.data?.data) {
      yield put(userSignOut());
    }
  } catch (error) {
    yield put(
      actionCreators.chargeInhouseProcessSuccess({
        status: false,
        action: 'guestInhouseDetail',
        message: error,
        result_code: 'ERROR',
      })
    );
  }
  yield delay(100);
}

function* chargeInhouseRequest() {
  yield takeLatest(actionTypes.CHARGE_INHOUSE_REQUEST, chargeInhouseRequestProcess);
}

function* chargeInhouseBreakdown() {
  yield takeLatest(
    actionTypes.CHARGE_INHOUSE_BREAKDOWN_REQUEST,
    chargeInhouseBreakdownProcess
  );
}

function* chargeInhouseUpdate() {
  yield takeEvery(actionTypes.CHARGE_INHOUSE_UPDATE, chargeInhouseUpdateProcess);
}

function* chargeInhouseDetail() {
  yield takeEvery(actionTypes.CHARGE_INHOUSE_DETAIL_REQUEST, chargeInhouseDetailProcess);
}

export default function* rootSaga() {
  yield all([
    fork(chargeInhouseRequest),
    fork(chargeInhouseBreakdown),
    fork(chargeInhouseUpdate),
    fork(chargeInhouseDetail),
  ]);
}
